import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Button, Chip, Dialog, DialogContent, Divider, Grid, IconButton, Pagination, TextField, Typography } from "@mui/material";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { Check, Close, WatchLater } from "@mui/icons-material";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonDialog from "../../common/components/CommonDialog";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { ExpierienceYears, ProfilesObj, ProjectStatus, ProjectTypeObj, ServiceOffer, UserTypeId } from "../../common/const";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { getMentorList } from "../../api/mentorApi";
import CommonBookSession from "../../common/components/CommonBookSession";
import moment from "moment";
import { confirmBooking, getEventByInvite } from "../../api/sessionApi";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { getProjectList, skipIntakeForm } from "../../api/projectApi";
import { createProjectResearch } from "../../api/resourceApi";

const ProjectType = {
    AIML: 'AI/ML',
    BIO: 'Bio Tech',
    ENGINEERING: 'Engineering',
    SCOCIAL: 'Social Science'
};

const typeObj = {
    'AI/ML': 'AI/ML',
    'Bio Tech': 'BIO_TECH',
    'Engineering': 'ENGINEERING',
    'Social Science': 'SOCIAL_SCIENCE'
};

const StyledPagination = styled(Pagination)({
    '& .MuiPaginationItem-root': {
        borderRadius: '8px',
        border: '1px solid #D1D5DB',
        color: '#374151',
        fontWeight: 500,
        minWidth: '40px',
        height: '40px',
        '&:hover': {
            borderColor: '#1E3A8A',
            color: '#1E3A8A',
            backgroundColor: '#F0F4FF',
        },
        '&.Mui-selected': {
            backgroundColor: '#1E3A8A',
            borderColor: '#1E3A8A',
            color: 'white',
            '&:hover': {
                backgroundColor: '#1E40AF',
            },
        },
    },
    '& .MuiPaginationItem-previousNext': {
        fontSize: '20px',
    },
});

const StyledChip = styled(Chip)({
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '20px',
    height: '28px',
});

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
    },
});

const ExploreProject = ({
    setType
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [projecType, setProjectType] = useState(ProjectType.AIML);
    const [successOpen, setSuccess] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [open, setOpen] = useState(false);
    const [meetingLink, setMeetingLink] = useState(null);
    const [initialData, setInitialData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [bookSessionDialog, setBookSessionDialog] = useState(false);
    const [sessionNote, setSessionNote] = useState('');
    const [mentorId, setMentorId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [mentors, setMentors] = useState(null);
    const [imageUrls, setImageUrls] = useState({});
    const itemsPerPage = 4;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, mentors?.data?.length);

    const [page, setPage] = useState(1);
    const [projects, setProjects] = useState(null);
    const [projectImageUrls, setProjectImageUrls] = useState({});
    const rowPerPage = 4;
    const start = (page - 1) * rowPerPage + 1;
    const end = Math.min(page * rowPerPage, projects?.data?.length);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleProjectPageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getMentorData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getMentorList(
                currentPage,
                itemsPerPage,
                JSON.stringify([{ "column": "service_offers", "op": "has", "value": ServiceOffer.mentor }]),
                '',
                true
            );
            setMentors(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            setMentors(null);
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getMentorData();
    }, [currentPage]);

    // Function to load image as blob
    const loadImageAsBlob = async (avatarPath, userId) => {
        try {
            const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${avatarPath}&download=false`;
            const response = await fetch(fullUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            setImageUrls(prev => ({
                ...prev,
                [userId]: objectUrl
            }));
        } catch (error) {
            console.error('Error loading image:', error);
        }
    };

    // Load all images when mentors are set
    useEffect(() => {
        if (mentors?.data?.length > 0) {
            mentors?.data?.map(mentor => {
                if (mentor.user?.avatar && !imageUrls[mentor.user_id]) {
                    loadImageAsBlob(mentor.user.avatar, mentor.user_id);
                }
            });
        }
    }, [mentors]);

    useEffect(() => {
        return () => {
            Object.values(imageUrls)?.forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, []);

    const getEventData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getEventByInvite(initialData?.payload?.invitee?.uri);
            setEventData(response.data);
            setBookSessionDialog(true);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (initialData !== null) {
            dispatch(setLoading({
                loading: true
            }));
            setTimeout(() => {
                getEventData();
            }, 5000);
        }
    }, [initialData]);

    const handleCloseAll = () => {
        setBookSessionDialog(false);
        setSessionNote('');
        setEventData(null);
    };

    const handleConfirmBooking = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const projectResponse = await createProjectResearch({
                "project_profile": ProfilesObj.Project,
                "images": selectedProject.images,
                "categories": selectedProject.categories,
                "project_name": selectedProject.project_name,
                "project_source": selectedProject.project_type,
                "project_bank_id": selectedProject.project_bank_id,
                "is_it_first_time": true,
                "status": ProjectStatus.Enrolled,
                "total_hours": selectedProject.total_hours,
                "is_active": true,
                "start_date": new Date().toISOString(),
                "student_id": user?.student_id,
                "goal": selectedProject.goal,
                "description": selectedProject.description,
                "mentor_profile_id": mentorId
            });
            const data = {
                "mentor_profile_id": mentorId,
                "student_id": user?.student_id,
                "service_offers": [ServiceOffer.mentor],
                "notes": sessionNote,
                "status": "CONFIRMED",
                "invitee_email": eventData.invitee_email,
                "calendly_event_uri": eventData.calendly_event_uri,
                "calendly_invitee_uri": eventData.calendly_invitee_uri,
                "project_id": projectResponse?.data?.project_id
            }
            const response = await confirmBooking(data);
            if (response) {
                dispatch(setLoading({
                    loading: false
                }));
                setBookSessionDialog(false);
                setSessionNote(null);
                setEventData(null);
                setInitialData(null);
                setOpenDialog(true);
                setMeetingLink(null);
                setMentorId(null);
                showNotification('Session booked successfully', 'success');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getProjectData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getProjectList(1, 4, '', '', typeObj[projecType]);
            setProjects(response?.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getProjectData();
    }, [projecType, page]);

    // Function to load image as blob
    const loadProjectImageAsBlob = async (avatarPath, userId) => {
        try {
            const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${avatarPath}&download=false`;
            const response = await fetch(fullUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            setProjectImageUrls(prev => ({
                ...prev,
                [userId]: objectUrl
            }));
        } catch (error) {
            console.error('Error loading image:', error);
        }
    };

    // Load all images when mentors are set
    useEffect(() => {
        if (projects?.data?.length > 0) {
            projects?.data?.map(mentor => {
                if (mentor.images && !projectImageUrls[mentor.project_bank_id]) {
                    loadProjectImageAsBlob(mentor.images.url, mentor.project_bank_id);
                }
            });
        }
    }, [projects, projecType, page]);

    useEffect(() => {
        return () => {
            Object.values(projectImageUrls)?.forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, []);


    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseAll}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: '#6B7280',
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                        Confirm Your Session
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <img
                                    src='/Images/graycalendar.png'
                                    style={{
                                        width: 20,
                                        position: 'relative'
                                    }}
                                    alt=''
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Date
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Time
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            Any specific question for this session
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Write here"
                            value={sessionNote}
                            onChange={(e) => setSessionNote(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Box>

                    <Button
                        fullWidth
                        onClick={handleConfirmBooking}
                        sx={{
                            backgroundColor: '#1E3A8A',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '8px',
                            height: '48px',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#1E40AF',
                            },
                        }}
                    >
                        Confirm Booking
                    </Button>
                </DialogContent>
            </StyledDialog>
            <CommonBookSession
                bookingUrl={meetingLink}
                open={open}
                setOpen={setOpen}
                studentName={`${user?.first_name} ${user?.last_name}`}
                email={user?.email}
                handleClose={(data) => {
                    setOpen(false);
                    setInitialData(data);
                }}
            />
            <CommonDialog
                open={openDialog}
                size="xs"
                content={(
                    <center>
                        <img
                            src='/Images/success.png'
                            alt='Check Circle'
                            style={{ width: '70px', height: '70px', marginTop: 20 }}
                        />
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5 }}>
                            Demo Scheduled Success
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph mt={0.3}>
                            You have successfully scheduled your demonstration with our expert Project Mentor. They look forward to working with you.
                        </Typography>
                        <Divider />
                        <CommonCard
                            borderRadius={1}
                            border={`1px solid #FF101052`}
                            background="#FFE6E6"
                            padding="8px"
                            content={
                                <Box>
                                    <Typography fontWeight={600} textAlign="left">
                                        Intake Form Notice:
                                    </Typography>
                                    <Typography color="red" textAlign="left" fontWeight={600} fontSize={13}>
                                        To better serve you and make the demonstration more meaningful, please complete the Project Intake Form.
                                    </Typography>
                                    <Typography color="red" textAlign="left" fontSize={13}>
                                        If you can't do it now, no worries—we have sent the link to your email.
                                    </Typography>
                                </Box>
                            }
                        />
                        <CommonButton
                            label="Fill Intake Form Now"
                            size="large"
                            marginTop={2}
                            marginBottom={2}
                            handleClick={async () => {
                                setOpenDialog(false);
                                setSelectedProject(null);
                                navigate('/project-intake-form');
                            }}
                        />
                        <CommonButton
                            label="I'll Do It Later"
                            size="large"
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await skipIntakeForm({
                                        student_id: user?.student_id
                                    });
                                    if (response.success) {
                                        setType('DCP Projects');
                                        setOpenDialog(false);
                                        setSelectedProject(null);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                            color={theme.palette.background.dark}
                            border={`1px solid #0000001A`}
                            background="Transparent"
                            marginBottom="0px"
                        />
                    </center>
                )}
            />
            <CommonDialog
                open={successOpen}
                size="xs"
                title={
                    <Fragment>
                        <Close
                            sx={{
                                color: theme.palette.background.dark, fontSize: 20,
                                float: 'right',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                setSuccess(false);
                            }}
                        />
                    </Fragment>
                }
                content={(
                    <center>
                        <img
                            src='/Images/success.png'
                            alt='Check Circle'
                            style={{ width: '70px', height: '70px', marginTop: 20 }}
                        />
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5 }}>
                            Project Added Successfully
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph mt={0.3}>
                            Project added to your portfolio successfully!
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <CommonButton
                        label="Return To Project"
                        size="small"
                        marginBottom="10px"
                        handleClick={() => {
                            setSuccess(false);
                        }}
                    />
                )}
            />
            <CommonCard
                paddingBottom="0px"
                borderRadius={2}
                padding="0px 13px 13px 13px"
                headerContent={
                    <Fragment>
                        <Box
                            sx={{ padding: '13px 0px 0px 13px' }}
                        >
                            <Typography fontWeight={600}>Find your passion project.</Typography>
                            <Typography fontSize={14} color={theme.palette.background.dark}>Find your next big project with DCP projects collection.</Typography>
                            <Box mt={2} display="flex" gap={1}>
                                <CommonButton
                                    label={ProjectType.AIML}
                                    width="100px"
                                    fontWeight={600}
                                    color={projecType === ProjectType.AIML ? theme.palette.secondary.main : theme.palette.background.dark}
                                    background={projecType === ProjectType.AIML ? theme.palette.primary.main : theme.palette.background.main}
                                    handleClick={() => setProjectType(ProjectType.AIML)}
                                    marginBottom="0px"
                                    height="35px"
                                />
                                <CommonButton
                                    label={ProjectType.BIO}
                                    width="100px"
                                    fontWeight={600}
                                    color={projecType === ProjectType.BIO ? theme.palette.secondary.main : theme.palette.background.dark}
                                    background={projecType === ProjectType.BIO ? theme.palette.primary.main : theme.palette.background.main}
                                    handleClick={() => setProjectType(ProjectType.BIO)}
                                    marginBottom="0px"
                                    height="35px"
                                />
                                <CommonButton
                                    label={ProjectType.ENGINEERING}
                                    width="100px"
                                    fontWeight={600}
                                    color={projecType === ProjectType.ENGINEERING ? theme.palette.secondary.main : theme.palette.background.dark}
                                    background={projecType === ProjectType.ENGINEERING ? theme.palette.primary.main : theme.palette.background.main}
                                    handleClick={() => setProjectType(ProjectType.ENGINEERING)}
                                    marginBottom="0px"
                                    height="35px"
                                />
                                <CommonButton
                                    label={ProjectType.SCOCIAL}
                                    width="140px"
                                    fontWeight={600}
                                    color={projecType === ProjectType.SCOCIAL ? theme.palette.secondary.main : theme.palette.background.dark}
                                    background={projecType === ProjectType.SCOCIAL ? theme.palette.primary.main : theme.palette.background.main}
                                    handleClick={() => setProjectType(ProjectType.SCOCIAL)}
                                    marginBottom="0px"
                                    height="35px"
                                />
                            </Box>
                        </Box>
                    </Fragment>
                }
                content={
                    <Fragment>
                        {projects?.data?.length > 0 ? (
                            <Grid container spacing={2}>
                                {projects?.data?.map((a, index) => (
                                    <Grid item md={3} key={index} onClick={() => {
                                        if (user?.user_type_id === UserTypeId.Student) {
                                            setSelectedProject(a);
                                        }
                                    }}>
                                        <CommonCard
                                            paddingBottom="0px"
                                            padding="0px"
                                            borderRadius={1}
                                            border={selectedProject?.project_bank_id === a?.project_bank_id ? `1px solid #10AE2D` : `1px solid #0000001A`}
                                            content={
                                                <Box p={2}>
                                                    <Box sx={{
                                                        position: 'relative',
                                                        width: '100%',
                                                    }}>
                                                        <img
                                                            src={projectImageUrls[a?.project_bank_id] ? projectImageUrls[a?.project_bank_id] : `https://ui-avatars.com/api/?name=${encodeURIComponent(`${a?.project_name}`)}&background=FAFAFAf1&color=fff`}
                                                            alt="profile"
                                                            height="150px"
                                                            width="100%"
                                                            style={{
                                                                borderRadius: 10,
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                        {selectedProject?.project_bank_id === a?.project_bank_id && (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '50%',
                                                                background: '#01875F',
                                                                width: 20,
                                                                height: 20,
                                                                position: 'absolute',
                                                                top: 8,
                                                                right: 8,
                                                                zIndex: 1,
                                                            }}>
                                                                <Check sx={{ color: 'white', fontSize: 12 }} />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Box>
                                                        <Typography fontWeight={600} fontSize={16}>{a.project_name}</Typography>
                                                        <Typography color="background.dark" sx={{ fontSize: 14 }}>
                                                            {a.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            }
                                            footerContent={
                                                user?.user_type_id === UserTypeId.Student &&
                                                (<CommonButton
                                                    label="Start Project"
                                                    height="35px"
                                                    marginTop="0px"
                                                    marginBottom="5px"
                                                    fontWeight={700}
                                                    handleClick={async (e) => {
                                                        try {
                                                            e.stopPropagation();
                                                            dispatch(setLoading({
                                                                loading: true
                                                            }));
                                                            const response = await createProjectResearch({
                                                                "project_profile": ProfilesObj.Project,
                                                                "images": a.images,
                                                                "categories": a.categories,
                                                                "project_name": a.project_name,
                                                                "project_source": a.project_type,
                                                                "project_bank_id": a.project_bank_id,
                                                                "is_it_first_time": true,
                                                                "status": ProjectStatus.Enrolled,
                                                                "total_hours": a.total_hours,
                                                                "is_active": true,
                                                                "start_date": new Date().toISOString(),
                                                                "student_id": user?.student_id,
                                                                "goal": a.goal,
                                                                "description": a.description
                                                            });
                                                            if (response.success) {
                                                                setSuccess(true);
                                                                setType('DCP Projects');
                                                                dispatch(setLoading({
                                                                    loading: false
                                                                }));
                                                            }
                                                        } catch (err) {
                                                            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                            showNotification(errorMessage, 'error');
                                                            setMentors(null);
                                                            dispatch(setLoading({
                                                                loading: false
                                                            }));
                                                        }
                                                    }}
                                                />)
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Typography textAlign="center" color={theme.palette.background.dark}>No Data Found</Typography>
                        )}
                        {projects?.pagination?.totalPages > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 2,
                                    pt: 2,
                                    borderTop: '1px solid #E5E7EB',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                    Showing {start}-{end} from {projects?.data?.length} data
                                </Typography>
                                <StyledPagination
                                    count={projects?.pagination?.totalPages}
                                    page={page}
                                    onChange={handleProjectPageChange}
                                    shape="rounded"
                                    siblingCount={1}
                                    boundaryCount={1}
                                />
                            </Box>
                        )}
                    </Fragment>
                }
            />
            <CommonCard
                paddingBottom="0px"
                borderRadius={2}
                padding="0px 13px 13px 13px"
                headerContent={
                    <Fragment>
                        <Box
                            sx={{ padding: '13px 0px 0px 13px' }}
                        >
                            <Typography fontWeight={600}>Available Mentors</Typography>
                            <Typography fontSize={14} color={theme.palette.background.dark}>Book a demo with out expert project mentors.</Typography>
                        </Box>
                    </Fragment>
                }
                content={
                    mentors?.data?.length > 0 ?
                        (
                            <Fragment>
                                <Grid container spacing={2}>
                                    {mentors?.data?.map((a, index) => (
                                        <Grid item md={3} sm={6} key={index}>
                                            <CommonCard
                                                paddingBottom="0px"
                                                padding="0px"
                                                borderRadius={2}
                                                border={`1px solid #0000001A`}
                                                content={
                                                    <Box p={2}>
                                                        <img
                                                            src={imageUrls[a.user_id] || `https://ui-avatars.com/api/?name=${encodeURIComponent(`${a?.mentor?.user?.first_name} ${a?.mentor?.user?.last_name}`)}&background=FAFAFAf1&color=fff`}
                                                            alt={`${a?.user?.first_name} ${a?.user?.last_name ? a?.user?.last_name : ''}`}
                                                            height="auto"
                                                            width="40%"
                                                        />
                                                        <Box>
                                                            <Typography fontWeight={600} fontSize={20}>{`${a?.user?.first_name} ${a?.user?.last_name ? a?.user?.last_name : ''}`}</Typography>
                                                            <Typography color="secondary.main" sx={{ fontSize: 14 }}>
                                                                {a?.degree}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                                {a.experience_years ? `${ExpierienceYears.find((i) => i.value === a.experience_years)?.label} Experience` : '-'}
                                                            </Typography>
                                                            <Typography mt={1} color="background.dark" fontSize={14}>Subject Expertise</Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                                                {a?.expertise?.map((tag, index) => (
                                                                    <StyledChip key={index} label={tag} size="small" />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                        {user?.user_type_id === UserTypeId.Student &&
                                                            (<CommonButton
                                                                label="Book A Project Demo"
                                                                marginTop={2}
                                                                marginBottom="0px"
                                                                fontWeight={600}
                                                                height='35px'
                                                                handleClick={() => {
                                                                    if (selectedProject !== null) {
                                                                        setOpen(true);
                                                                        setMeetingLink(a.calendly_meeting_link);
                                                                        setMentorId(a.mentor_profile_id);
                                                                    } else {
                                                                        showNotification('Please select project before book a demo', 'error');
                                                                    }
                                                                }}
                                                            />)}
                                                    </Box>
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                {mentors?.pagination?.totalPages > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mt: 2,
                                            pt: 2,
                                            borderTop: '1px solid #E5E7EB',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                            Showing {startItem}-{endItem} from {mentors?.data?.length} data
                                        </Typography>
                                        <StyledPagination
                                            count={mentors?.pagination?.totalPages}
                                            page={currentPage}
                                            onChange={handlePageChange}
                                            shape="rounded"
                                            siblingCount={1}
                                            boundaryCount={1}
                                        />
                                    </Box>
                                )}
                            </Fragment>) : (
                            <Typography textAlign="center" color={theme.palette.background.dark}>No Data Found</Typography>
                        )
                }
            />
        </Fragment>
    )
}

export default ExploreProject;